import { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { VisitStatus, Host, Visit } from 'store/visits/types';
import { canCancelVisit, canEditVisit } from 'utils/visit-status-rules';
import { MonthlyMode } from 'components/create-visit-form-v2/types/form-data.types';
import { VisitorFrequencyEnum } from 'components/visit-form/types';
import { Card, Button, Skeleton } from '@hqo/ui-atoms';
import { Calendar, Clock, MapPin, User, Pencil, XCircle, Repeat } from 'lucide-react';
import { VisitStatusBadge } from 'components/shared/visit-status-badge';
import { useEditVisitAction } from 'hooks/use-edit-visit-action.hook';
import { useCancelVisitAction } from 'hooks/use-cancel-visit-action.hook';
import { useFieldMetadata } from 'store/ui-metadata/hooks';
import { FeatureEnablementFlagEnum } from 'store/ui-metadata/types';
import { useDateTimeFormatters } from 'shared/hooks';
import { useScheduleText } from 'shared/hooks/use-schedule-text';
import { CancelVisitConfirmation } from './cancel-visit-confirmation';

interface VisitCardV2Props {
  id?: string;
  first_name?: string;
  last_name?: string;
  visit_status?: VisitStatus;
  arrival_date?: Date;
  departure_date?: Date;
  floor?: string;
  suite?: string;
  host?: Host;
  frequency?: VisitorFrequencyEnum;
  recurrence_days?: Visit['recurrence_days'];
  end_date?: Date;
  count?: number;
  monthly_mode?: MonthlyMode | null;
  monthly_on_last_day?: boolean | null;
  monthly_by_day?: { position: number; weekday: string } | null;
  isLoading?: boolean;
  vendor_company_uuid?: string | null;
  is_group_visit?: boolean;
}

export const VisitCardV2 = ({
  id,
  first_name,
  last_name,
  visit_status,
  arrival_date,
  departure_date,
  floor,
  suite,
  host,
  frequency,
  recurrence_days,
  end_date,
  count,
  monthly_mode,
  monthly_on_last_day,
  monthly_by_day,
  isLoading = false,
  vendor_company_uuid,
  is_group_visit,
}: VisitCardV2Props): JSX.Element => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { formatNumericDate, formatTimeRange } = useDateTimeFormatters();
  const handleEdit = useEditVisitAction(id);
  const handleCancelConfirm = useCancelVisitAction(id);
  const cancelVisitsEnabled = useFieldMetadata('cancel_visits_enabled');
  const editVisitsEnabled = useFieldMetadata('visit_editing_enabled');
  const floorEnabled = useFieldMetadata('floor');
  const suiteEnabled = useFieldMetadata('suite');
  const isVendorOrGroupVisit = Boolean(vendor_company_uuid) || Boolean(is_group_visit);
  const showEditAction = editVisitsEnabled && !isVendorOrGroupVisit;
  // Same rule as the table row and the detail page, so no two surfaces disagree about what is still possible.
  // Both actions address the visit by id; without one they render as buttons that do nothing.
  const canEdit = Boolean(id) && showEditAction && canEditVisit(visit_status);
  const canCancel = Boolean(id) && cancelVisitsEnabled && canCancelVisit(visit_status);

  const visitIsRecurring = useMemo(
    () => Object.values(recurrence_days || {}).some(Boolean) || Boolean(monthly_mode),
    [recurrence_days, monthly_mode],
  );
  const scheduleText = useScheduleText(
    visitIsRecurring
      ? {
          frequency,
          recurrence_days,
          arrival_date,
          end_date,
          count,
          monthly_mode,
          monthly_on_last_day,
          monthly_by_day,
        }
      : null,
  );

  const showFloor = floor && floorEnabled !== FeatureEnablementFlagEnum.DISABLED;
  const showSuite = suite && suiteEnabled !== FeatureEnablementFlagEnum.DISABLED;
  const showLocation = showFloor || showSuite;

  const handleCancelClick = useCallback(() => {
    setShowCancelModal(true);
  }, []);

  const handleCancelModalClose = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    handleCancelConfirm();
    setShowCancelModal(false);
  }, [handleCancelConfirm]);

  if (isLoading) {
    return (
      <Card>
        <div className="px-5">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          </div>

          <div className="space-y-2.5 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <Skeleton className="h-4 w-32" />
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <Skeleton className="h-4 w-40" />
            </div>

            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-gray-400" />
              <Skeleton className="h-4 w-36" />
            </div>

            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-400" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="px-5">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-2 text-gray-900">
              {/*
                Every prop on this card is optional — it doubles as its own loading skeleton — so an
                absent id would render `to={undefined}`, which links to the page you are already on.
              */}
              {id ? (
                <Link to={id} className="text-gray-900 hover:underline" data-testid="visit-card-name-link">
                  {first_name} {last_name}
                </Link>
              ) : (
                <span data-testid="visit-card-name-text">
                  {first_name} {last_name}
                </span>
              )}
            </h3>
            <VisitStatusBadge status={visit_status} />
          </div>
          {(canEdit || canCancel) && (
            <div className="flex gap-1">
              {canEdit && (
                <Button variant="ghost" size="icon" onClick={handleEdit} className="h-8 w-8">
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCancelClick}
                  className="h-8 w-8 text-destructive hover:text-destructive"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2.5 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <div className="flex items-center gap-1">
              <span>{formatNumericDate(arrival_date)}</span>
              {frequency && frequency !== VisitorFrequencyEnum.ONCE && <Repeat className="h-4 w-4 text-gray-500" />}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span>{formatTimeRange(arrival_date, departure_date)}</span>
          </div>

          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-400" />
            <span className="text-gray-600">
              {host.first_name} {host.last_name}
            </span>
          </div>

          {showLocation && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-gray-400" />
              <span>
                {showFloor && `Floor ${floor}`}
                {showFloor && showSuite && ', '}
                {showSuite && `Suite ${suite}`}
              </span>
            </div>
          )}
        </div>
      </div>
      {showCancelModal && (
        <CancelVisitConfirmation
          onCancel={handleCancelModalClose}
          onConfirm={handleConfirmCancel}
          visitorName={`${first_name} ${last_name}`}
          arrivalDate={arrival_date}
          departureDate={departure_date}
          isRecurring={visitIsRecurring}
          scheduleText={scheduleText}
        />
      )}
    </Card>
  );
};
